import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { FingoDialog } from './index';
import useIsMobile from '../../hooks/useIsMobile';
import RestrictionItemWithDocumentUpload from '../lists/RestrictionItemWithDocumentUpload';
import RestrictionItem from '../lists/RestrictionItem';
import { DEFAULT_ICON_CLASS_NAME } from '../../theme/constants/icon';
import { ICON_STACK_CLASS_NAME } from '../../theme/constants/stack';

import PendingDocumentsDialogFooter from './PendingDocumentsDialogFooter';

const PendingDocumentsDialog = ({
  customDialogTitle,
  customDialogSubtitle,
  invoiceId,
  open,
  onClose,
  type,
  filteredRestrictions,
  executiveAssigned,
  loading,
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile('tablet');
  const dialogTitle = {
    Risk: 'Documentos Pendientes',
    Operations: 'Requisitos de giro',
  };
  const dialogSubtitle = {
    Risk: 'Para poder avanzar con la evaluación necesitamos aclarar los siguientes aspectos:',
    Operations:
      'Al ceder esta factura se te pedirá regularizar los siguientes aspectos, posterior a ello se realizará el giro',
  };
  const [requireDocumentUploadRestrictions,
    notRequireDocumentUploadRestrictions] = useMemo(() => filteredRestrictions.reduce(
    ([withUpload, withoutUpload], detail) => (
      detail.requireDocumentUpload
        ? [[...withUpload, detail], withoutUpload]
        : [withUpload, [...withoutUpload, detail]]
    ),
    [[], []],
  ), [filteredRestrictions]);

  return (
    <FingoDialog
      titleBackgroundColor="white"
      topIcon={{
        icon: theme.branding.icons.information,
        props: {
          className: DEFAULT_ICON_CLASS_NAME,
          svgWrapperClassName: ICON_STACK_CLASS_NAME,
          sx: { width: 40, fontSize: 40 },
        },
      }}
      title={(
        <Typography
          color="text.emphasis"
          textAlign="center"
          variant="large_1"
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            textAlign: 'center',
          }}
        >
          {customDialogTitle || dialogTitle[type]}
        </Typography>
    )}
      subtitle={(
        <Typography
          variant={isMobile ? 'small_2' : 'medium_1'}
          color="gray.A500"
          textAlign="center"
          sx={{
            mb: { xs: 1, md: 2 },
            px: { xs: 1, md: 2 },
            py: 0,
            display: 'flex',
            justifyContent: 'center',
            width: '100%',
          }}
        >
          {customDialogSubtitle || dialogSubtitle[type]}
        </Typography>
      )}
      open={open}
      onClose={onClose}
      handleClose={onClose}
      maxWidth={700}
      fullWidth
      paddingContent={isMobile ? 3 : 4}
    >
      <Stack spacing={2.5}>
        <Stack spacing={1}>
          {notRequireDocumentUploadRestrictions.map((detail) => (
            <RestrictionItem
              key={detail.id}
              detail={detail}
              isMobile={isMobile}
            />
          ))}
          {requireDocumentUploadRestrictions.map((detail) => (
            <RestrictionItemWithDocumentUpload
              key={detail.id}
              detail={detail}
              isMobile={isMobile}
              invoiceId={invoiceId}
            />
          ))}
          {loading && (
            <RestrictionItem
              detail={{}}
              isMobile={isMobile}
              isSkeleton
            />
          )}
          {requireDocumentUploadRestrictions.length > 0 && (
          <Stack direction="row" spacing={1} justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              size="small_1"
              sx={{ width: 117 }}
              onClick={onClose}
            >
              <Typography variant="small_2" fontWeight={700}>
                Enviar
              </Typography>
            </Button>
          </Stack>
          )}
        </Stack>
        <PendingDocumentsDialogFooter
          executiveAssigned={executiveAssigned}
        />
      </Stack>
    </FingoDialog>
  );
};

const detail = PropTypes.shape({
  id: PropTypes.string.isRequired,
  restrictionLabel: PropTypes.string,
  description: PropTypes.string,
  status: PropTypes.string,
  statusLabel: PropTypes.string,
  requireDocumentUpload: PropTypes.bool,
  documentInfo: PropTypes.shape({
    documentType: PropTypes.string,
    accept: PropTypes.string,
    errorMessage: PropTypes.string,
  }),
});

PendingDocumentsDialog.propTypes = {
  customDialogTitle: PropTypes.string,
  customDialogSubtitle: PropTypes.string,
  invoiceId: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(['Risk', 'Operations']).isRequired,
  filteredRestrictions: PropTypes.arrayOf(detail),
  executiveAssigned: PropTypes.shape({
    id: PropTypes.string.isRequired,
    completeName: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    phoneNumber: PropTypes.string.isRequired,
  }),
  loading: PropTypes.bool,
};

PendingDocumentsDialog.defaultProps = {
  customDialogTitle: null,
  customDialogSubtitle: null,
  invoiceId: null,
  filteredRestrictions: [],
  executiveAssigned: null,
  loading: false,
};

export default PendingDocumentsDialog;
